/** @format */
import { CalendarEvent, Interval, LocalDateTimeString } from 'common-typescript/types';
import * as _ from 'lodash-es';
import moment from 'moment';

const MAX_EVENTS = 365 as const;

export function createEventInstances(calendarEvent: CalendarEvent): { start: Date; end: Date; excluded: boolean }[] {
    if (!calendarEvent || !calendarEvent.startTime || !calendarEvent.duration) {
        return [];
    }
    const events = [];
    const startDateTime = moment(calendarEvent.startTime);
    if (calendarEvent.recursEvery === 'NEVER') {
        events.push(createEvent(startDateTime.clone(), calendarEvent));
    } else {
        const recursUntil = moment(calendarEvent.recursUntil);
        let start = startDateTime.clone();
        for (let index = 0; index < MAX_EVENTS + 1 && start.isSameOrBefore(recursUntil, 'day'); index += 1) {
            events.push(createEvent(start, calendarEvent));
            start = nthEventStartDate(calendarEvent.recursEvery, startDateTime, index + 1);
        }
    }
    const eventStartDates = _.map(events, 'start');
    calendarEvent.exceptions = _.filter(calendarEvent.exceptions, exceptionDate => containsDate(eventStartDates, exceptionDate));
    return events;
}

export function createEvent(startTime: moment.Moment, calendarEvent: CalendarEvent): { start: Date; end: Date; excluded: boolean } {
    const end = getCalendarEventEndTime(calendarEvent, startTime);
    return {
        start: startTime.clone().toDate(),
        end: end.toDate(),
        excluded: isExcluded(calendarEvent, startTime),
    };
}

export function getCalendarEventEndTime(calendarEvent: CalendarEvent, startTime: moment.Moment): moment.Moment {
    const start = startTime || moment(calendarEvent.startTime);
    return start.clone().add(moment.duration(calendarEvent.duration).asMinutes(), 'minutes');
}

function isExcluded(studyEvent: CalendarEvent, date: moment.Moment): boolean {
    return containsDate(studyEvent.exceptions, date);
}

function containsDate(dateArray: (Date | LocalDateTimeString)[], date: moment.Moment | LocalDateTimeString): boolean {
    return !!_.find(dateArray, arrayDate => datesEqual(arrayDate, date));
}

function datesEqual(date1: LocalDateTimeString | Date, date2: moment.Moment | LocalDateTimeString): boolean {
    const event1Date = moment(date1);
    const event2Date = moment(date2);
    return event1Date.isSame(event2Date, 'day');
}

/**
 * Start date of the nth occurrence, always counted from the first occurrence rather than from the previous one.
 * Otherwise a MONTHLY series starting on a day that a later month does not have would drag the clamped day through
 * the rest of the series (31.1. -> 28.2. -> 28.3. -> ...) instead of returning to the original day of month.
 * Must stay in sync with StudyEvent#getEventInstances and CalendarEvent#getEventInstances in the backend.
 */
function nthEventStartDate(type: Interval, firstStartDate: moment.Moment, n: number): moment.Moment {
    const nthStartDate = firstStartDate.clone();
    if (type === 'DAILY') {
        nthStartDate.add(n, 'days');
    } else if (type === 'WEEKLY') {
        nthStartDate.add(7 * n, 'days');
    } else if (type === 'EVERY_SECOND_WEEK') {
        nthStartDate.add(14 * n, 'days');
    } else if (type === 'MONTHLY') {
        nthStartDate.add(n, 'months');
    }
    return nthStartDate;
}
